import { useCallback, useEffect, useMemo, useState } from 'react'
import type {
  BusinessEntity,
  CpaShare,
  FinanceDocument,
  FinancialAccount,
} from '../../types/family-finance'
import {
  getEstimatorInputs,
  inviteCpa,
  listAccounts,
  listCpaShares,
  listDocuments,
  listEntities,
  revokeCpaShare,
  saveEstimatorInputs,
} from '../../lib/family-finance-api'
import { estimateFederal, type TaxEstimateInputs } from '../../lib/tax-estimator'
import DocumentUploader from './DocumentUploader'
import { DocumentList } from './DocumentsTab'
import {
  Card,
  Chip,
  EmptyState,
  LoadError,
  LoadingRows,
  errorMessage,
  inputClass,
  money,
  pct,
  primaryBtn,
  shortDate,
} from './shared'

// ─── Estimator ───────────────────────────────────────────────

function NumberField({
  label,
  value,
  onChange,
}: {
  label: string
  value: number
  onChange: (v: number) => void
}) {
  return (
    <label className="block">
      <span className="text-slate-500 dark:text-slate-400 text-xs">{label}</span>
      <input
        type="number"
        value={Number.isFinite(value) ? value : 0}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        className={`${inputClass} mt-1`}
      />
    </label>
  )
}

function EstimatorCard() {
  const [inputs, setInputs] = useState<TaxEstimateInputs | null>(null)
  const [editing, setEditing] = useState(false)
  const [saved, setSaved] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    setLoadError(null)
    getEstimatorInputs()
      .then(setInputs)
      .catch((err) => setLoadError(errorMessage(err)))
  }, [reloadKey])

  const result = useMemo(() => (inputs ? estimateFederal(inputs) : null), [inputs])

  if (loadError) return <LoadError error={loadError} onRetry={() => setReloadKey((k) => k + 1)} />
  if (!inputs || !result) return <LoadingRows rows={3} />

  function update(patch: Partial<TaxEstimateInputs>) {
    setInputs((prev) => (prev ? { ...prev, ...patch } : prev))
    setSaved(false)
  }

  async function persist() {
    if (!inputs) return
    await saveEstimatorInputs(inputs)
    setSaved(true)
  }

  const refund = result.refundOrOwed

  return (
    <Card
      title="Estimated Federal Return — Tax Year 2026 (YTD)"
      subtitle="Planning estimate only — not tax advice. Texas has no personal income tax."
      action={
        <button onClick={() => setEditing((v) => !v)} className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm">
          {editing ? 'Hide inputs' : 'Edit inputs'}
        </button>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className={`rounded-xl p-4 border ${refund >= 0 ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
          <p className="text-slate-600 dark:text-slate-300 text-sm">{refund >= 0 ? 'Projected refund' : 'Projected balance due'}</p>
          <p className={`text-3xl font-bold mt-1 ${refund >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
            {money(Math.abs(refund))}
          </p>
          <p className="text-slate-500 dark:text-slate-400 text-xs mt-2">
            Effective rate {pct(result.effectiveRate)} · Marginal bracket {pct(result.marginalRate, 0)}
          </p>
          {refund < 0 && (
            <p className="text-amber-600/90 dark:text-amber-400/90 text-xs mt-2">
              Consider raising Q3/Q4 1040-ES payments to avoid an underpayment penalty.
            </p>
          )}
        </div>

        <div className="lg:col-span-2">
          <div className="space-y-1.5">
            {result.lines.map((line) => (
              <div key={line.label} className="flex items-center justify-between text-sm py-1 border-b border-slate-200/40 dark:border-slate-700/40 last:border-0">
                <span className="text-slate-500 dark:text-slate-400">
                  {line.label}
                  {line.note && <span className="text-slate-400 dark:text-slate-600 text-xs ml-2">({line.note})</span>}
                </span>
                <span className={`font-medium ${line.amount < 0 ? 'text-emerald-600/90 dark:text-emerald-400/90' : 'text-slate-800 dark:text-slate-200'}`}>
                  {line.amount < 0 ? `−${money(Math.abs(line.amount))}` : money(line.amount)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {editing && (
        <div className="mt-5 pt-5 border-t border-slate-200 dark:border-slate-700">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <label className="block">
              <span className="text-slate-500 dark:text-slate-400 text-xs">Filing status</span>
              <select
                value={inputs.filingStatus}
                onChange={(e) => update({ filingStatus: e.target.value as 'mfj' | 'single' })}
                className={`${inputClass} mt-1`}
              >
                <option value="mfj">Married filing jointly</option>
                <option value="single">Single</option>
              </select>
            </label>
            <NumberField label="W-2 wages (household)" value={inputs.wages} onChange={(v) => update({ wages: v })} />
            <NumberField label="Federal withholding" value={inputs.federalWithholding} onChange={(v) => update({ federalWithholding: v })} />
            <NumberField label="Business / SE net income" value={inputs.seNetIncome} onChange={(v) => update({ seNetIncome: v })} />
            <NumberField label="Interest, dividends, gains" value={inputs.otherIncome} onChange={(v) => update({ otherIncome: v })} />
            <NumberField label="Pre-tax contributions" value={inputs.preTaxContributions} onChange={(v) => update({ preTaxContributions: v })} />
            <NumberField label="Itemized deductions (0 = std)" value={inputs.itemizedDeductions} onChange={(v) => update({ itemizedDeductions: v })} />
            <NumberField label="Children under 17" value={inputs.dependentsUnder17} onChange={(v) => update({ dependentsUnder17: v })} />
            <NumberField label="1040-ES paid so far" value={inputs.estimatedPaymentsMade} onChange={(v) => update({ estimatedPaymentsMade: v })} />
          </div>
          <div className="flex items-center gap-3 mt-3">
            <button onClick={persist} className={primaryBtn}>Save inputs</button>
            {saved && <span className="text-emerald-600 dark:text-emerald-400 text-sm">Saved ✓</span>}
            <span className="text-slate-500 text-xs">
              SE income pre-fills from entity P&Ls once the sync backend is live.
            </span>
          </div>
        </div>
      )}
    </Card>
  )
}

// ─── CPA sharing ─────────────────────────────────────────────

function CpaShareCard({ docs }: { docs: FinanceDocument[] }) {
  const [shares, setShares] = useState<CpaShare[]>([])
  const [loading, setLoading] = useState(true)
  const [showInvite, setShowInvite] = useState(false)
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [years, setYears] = useState('2025')
  const [error, setError] = useState<string | null>(null)
  const [sending, setSending] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)

  const reload = useCallback(async () => {
    try {
      setShares(await listCpaShares())
      setLoadError(null)
    } catch (err) {
      setLoadError(errorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    reload()
  }, [reload])

  const sharedCount = docs.filter((d) => d.sharedWithCpa).length

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!email.includes('@') || !name.trim()) {
      setError('A valid email and name are required.')
      return
    }
    setSending(true)
    try {
      await inviteCpa({
        email,
        name,
        taxYears: years
          .split(',')
          .map((y) => parseInt(y.trim(), 10))
          .filter((y) => !isNaN(y)),
      })
      setEmail('')
      setName('')
      setShowInvite(false)
      await reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invite failed')
    } finally {
      setSending(false)
    }
  }

  async function handleRevoke(share: CpaShare) {
    if (!window.confirm(`Revoke ${share.name}'s access? They will immediately lose the shared folder.`)) return
    await revokeCpaShare(share.id)
    await reload()
  }

  if (loadError) return <LoadError error={loadError} onRetry={reload} />

  return (
    <Card
      title="CPA Shared Folder"
      subtitle={`${sharedCount} document${sharedCount === 1 ? '' : 's'} in the shared folder — toggle 👁 below to add or remove`}
      action={
        <button onClick={() => setShowInvite((v) => !v)} className={primaryBtn}>
          {showInvite ? 'Close' : '+ Invite CPA'}
        </button>
      }
    >
      {showInvite && (
        <form onSubmit={handleInvite} className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4 p-4 bg-slate-100/60 dark:bg-slate-900/60 rounded-xl">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="CPA name" className={inputClass} />
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="cpa@firm.com" type="email" className={inputClass} />
          <input value={years} onChange={(e) => setYears(e.target.value)} placeholder="Tax years (e.g. 2025, 2024)" className={inputClass} />
          {error && <p className="text-red-600 dark:text-red-400 text-sm sm:col-span-3">{error}</p>}
          <div className="sm:col-span-3 flex items-center justify-between">
            <p className="text-slate-500 text-xs max-w-md">
              Your CPA gets an email invitation with read-only access to the shared folder for the chosen tax
              years — no access to accounts, budgets, or anything else. Access expires automatically in 180 days.
            </p>
            <button type="submit" disabled={sending} className={primaryBtn}>
              {sending ? 'Sending…' : 'Send invitation'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <LoadingRows rows={1} />
      ) : shares.length === 0 ? (
        <EmptyState icon="🧑‍💼" message="No CPA has access yet. Invite yours to share the document folder securely — no more email attachments." />
      ) : (
        <div className="space-y-2">
          {shares.map((s) => (
            <div key={s.id} className="flex items-center justify-between py-2 px-3 bg-slate-100/50 dark:bg-slate-900/50 rounded-lg">
              <div>
                <p className="text-slate-800 dark:text-slate-200 text-sm font-medium">
                  {s.name} <span className="text-slate-500 font-normal">· {s.email}</span>
                </p>
                <p className="text-slate-500 text-xs">
                  Tax years {s.taxYears.join(', ')} · invited {shortDate(s.invitedAt)} · expires {shortDate(s.expiresAt)}
                  {s.lastAccess && ` · last opened ${shortDate(s.lastAccess)}`}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Chip tone={s.status === 'active' ? 'green' : s.status === 'invited' ? 'amber' : 'slate'}>{s.status}</Chip>
                {s.status !== 'revoked' && (
                  <button onClick={() => handleRevoke(s)} className="text-red-600/80 dark:text-red-400/80 hover:text-red-700 dark:hover:text-red-300 text-sm px-2">
                    Revoke
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}

// ─── Tax document hub ────────────────────────────────────────

export default function TaxesTab() {
  const [docs, setDocs] = useState<FinanceDocument[]>([])
  const [entities, setEntities] = useState<BusinessEntity[]>([])
  const [accounts, setAccounts] = useState<FinancialAccount[]>([])
  const [loading, setLoading] = useState(true)
  const [yearFilter, setYearFilter] = useState<number | 'all'>('all')
  const [showUpload, setShowUpload] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)

  const reload = useCallback(async () => {
    try {
      const [d, e, a] = await Promise.all([listDocuments(), listEntities(), listAccounts()])
      setDocs(d)
      setEntities(e)
      setAccounts(a)
      setLoadError(null)
    } catch (err) {
      setLoadError(errorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    reload()
  }, [reload])

  // This hub shows the Taxes folder; the Documents tab shows all three.
  const taxDocs = useMemo(() => docs.filter((d) => d.folder === 'taxes'), [docs])
  const years = useMemo(
    () =>
      Array.from(new Set(taxDocs.map((d) => d.taxYear).filter((y): y is number => y !== null))).sort(
        (a, b) => b - a,
      ),
    [taxDocs],
  )
  const filtered = yearFilter === 'all' ? taxDocs : taxDocs.filter((d) => d.taxYear === yearFilter)

  if (loadError) return <LoadError error={loadError} onRetry={reload} />
  if (loading) return <LoadingRows rows={6} />

  return (
    <div className="space-y-6">
      <EstimatorCard />
      <CpaShareCard docs={docs} />

      <Card
        title="Tax Document Hub"
        subtitle="W-2s, 1099s, K-1s, receipts, prior returns — uploads land here automatically when the filename looks tax-related"
        action={
          <div className="flex items-center gap-2">
            <select
              value={yearFilter}
              onChange={(e) => setYearFilter(e.target.value === 'all' ? 'all' : parseInt(e.target.value, 10))}
              className="bg-slate-100 dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-sm rounded-lg px-2 py-1.5 border border-slate-300 dark:border-slate-600 outline-none"
            >
              <option value="all">All years</option>
              {years.map((y) => (
                <option key={y} value={y}>TY {y}</option>
              ))}
            </select>
            <button onClick={() => setShowUpload((v) => !v)} className={primaryBtn}>
              {showUpload ? 'Close' : '+ Upload'}
            </button>
          </div>
        }
      >
        {showUpload && (
          <div className="mb-5">
            <DocumentUploader
              entities={entities}
              accounts={accounts}
              onUploaded={reload}
              lockTo={{ folder: 'taxes' }}
              hint="Files dropped here are filed under Taxes — the form type and tax year are read from the filename."
            />
          </div>
        )}

        <DocumentList
          docs={filtered}
          entities={entities}
          accounts={accounts}
          onChanged={reload}
          emptyMessage={
            yearFilter === 'all'
              ? 'No tax documents yet — upload W-2s, 1099s, or K-1s above.'
              : `Nothing filed for tax year ${yearFilter} yet.`
          }
        />
      </Card>
    </div>
  )
}
